/**
 * Accept textbook ISBNs with 9–13 digits.
 * Local publisher numbers often fail check-digit algorithms, so we validate
 * by length only (dashes/spaces ignored). ISBN-10 may end with X.
 */
export function isValidIsbn(raw: string): boolean {
  const s = raw.replace(/[-\s]/g, "").toUpperCase();
  if (!s) return false;
  // 9–13 digits, or 9 digits + X (ISBN-10 style check character)
  return /^\d{9,13}$/.test(s) || /^\d{9}X$/.test(s);
}

/**
 * Optional strict check-digit validation (ISBN-10 / ISBN-13).
 * Kept for tooling that needs algorithmic validation; catalog entry does not require it.
 */
export function hasValidIsbnCheckDigit(raw: string): boolean {
  const s = raw.replace(/[-\s]/g, "").toUpperCase();
  if (/^\d{13}$/.test(s)) return isValidIsbn13(s);
  if (/^\d{9}[\dX]$/.test(s)) return isValidIsbn10(s);
  return false;
}

function isValidIsbn10(isbn10: string): boolean {
  let sum = 0;
  for (let i = 0; i < 9; i++) {
    sum += parseInt(isbn10[i], 10) * (10 - i);
  }
  const check = isbn10[9] === "X" ? 10 : parseInt(isbn10[9], 10);
  return (sum + check) % 11 === 0;
}

function isValidIsbn13(isbn13: string): boolean {
  let sum = 0;
  for (let i = 0; i < 12; i++) {
    const d = parseInt(isbn13[i], 10);
    sum += d * (i % 2 === 0 ? 1 : 3);
  }
  const check = (10 - (sum % 10)) % 10;
  return check === parseInt(isbn13[12], 10);
}

/**
 * Accept journal ISSNs with 8–10 characters after stripping dashes/spaces.
 * Last character may be X (standard ISSN check character).
 */
export function isValidIssn(raw: string): boolean {
  const s = raw.replace(/[-\s]/g, "").toUpperCase();
  return /^\d{7,9}[\dX]$/.test(s);
}
